import React, { useCallback, useEffect, useMemo, useState } from 'react';
import toast from 'react-hot-toast';
import { getMailboxConfig, getMailboxSyncStatus, syncMailboxNow } from '../services/emailInboxApi';
import EmailInboxSyncBanner from '../components/EmailInbox/EmailInboxSyncBanner';
import EmailInboxList from '../components/EmailInbox/EmailInboxList';
import EmailInboxDetail from '../components/EmailInbox/EmailInboxDetail';
import { useAuth } from '../contexts/AuthContext';
import { useNotifications } from '../contexts/NotificationContext';
import { useRealtimeSocket } from '../hooks/useRealtimeSocket';

const CATEGORY_LABELS = {
  order_request: 'Order Request',
  quote_request: 'Quote Request',
  needs_review: 'Needs Review',
  blocked: 'Blocked',
  other: 'Other',
};

export default function OrderGenieEmailInbox() {
  const { account } = useAuth();
  const { addNotification } = useNotifications();
  const [syncStatus, setSyncStatus] = useState(null);
  const [syncing, setSyncing] = useState(false);
  const [refreshKey, setRefreshKey] = useState(0);
  const [deepLinkedItemId, setDeepLinkedItemId] = useState(null);
  const [mailboxEmail, setMailboxEmail] = useState('');

  useEffect(() => {
    getMailboxConfig()
      .then((res) => {
        const cfg = res?.config || {};
        setMailboxEmail(cfg.reply_smtp_from_email || cfg.imap_username || '');
      })
      .catch(() => {});
  }, []);

  // Auto-open a specific email item when ?item=<id> is in the URL
  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    const itemId = params.get('item');
    if (itemId) {
      setDeepLinkedItemId(itemId);
      // Clean the URL param without a page reload
      const clean = `${window.location.pathname}`;
      window.history.replaceState({}, '', clean);
    }
  }, []);

  const accountId = account?.accountId || account?.id || null;

  const loadStatus = useCallback(async () => {
    try {
      setSyncStatus(await getMailboxSyncStatus());
    } catch {}
  }, []);

  useEffect(() => { loadStatus(); }, [loadStatus]);

  // Polling fallback: keeps the inbox current even if the real-time socket
  // (which requires a separate websocket service to be reachable) is down.
  useEffect(() => {
    if (!accountId) return undefined;
    const interval = setInterval(() => {
      loadStatus();
      setRefreshKey((k) => k + 1);
    }, 30000);
    return () => clearInterval(interval);
  }, [accountId, loadStatus]);

  async function handleSyncNow() {
    setSyncing(true);
    try {
      await syncMailboxNow();
      setTimeout(async () => {
        await loadStatus();
        setRefreshKey((k) => k + 1);
      }, 3000);
    } catch {}
    finally { setSyncing(false); }
  }

  const socketEvents = useMemo(
    () => ['email:received', 'email:processing', 'email:classified', 'email:sync_done'],
    [],
  );

  const handleRealtimeEvent = useCallback((eventName, payload) => {
    if (eventName === 'email:received') {
      // Show the email the instant it's ingested — before classification/matching
      // even starts — so a large order doesn't look like it never arrived while
      // it's still being processed in the background.
      const from = payload.from_name || payload.from_email || 'Unknown sender';
      const subject = payload.subject || '(no subject)';
      toast(`New email: ${subject} (from ${from}) — processing…`, { duration: 4000, icon: '📥' });
      setRefreshKey((k) => k + 1);
    }

    if (eventName === 'email:processing') {
      setRefreshKey((k) => k + 1);
    }

    if (eventName === 'email:classified') {
      const catLabel = CATEGORY_LABELS[payload.category] || payload.category;
      const from = payload.from_name || payload.from_email || 'Unknown sender';
      const subject = payload.subject || '(no subject)';

      // Add to global notification bell
      addNotification({
        type: 'email_classified',
        title: subject,
        body: `From: ${from} — ${catLabel}`,
        category: payload.category,
        itemId: payload.item_id,
        linkTo: '/order-x/email-inbox',
      });

      // Toast only for actionable categories
      if (['order_request', 'quote_request', 'needs_review'].includes(payload.category)) {
        toast.success(`New ${catLabel}: ${subject}`, { duration: 5000 });
      }

      // Refresh the list to show the new email
      setRefreshKey((k) => k + 1);
    }

    if (eventName === 'email:sync_done') {
      loadStatus();
      setRefreshKey((k) => k + 1);
    }
  }, [addNotification, loadStatus]);

  const { isConnected } = useRealtimeSocket({
    enabled: Boolean(accountId),
    room: `email-inbox-${accountId}`,
    events: socketEvents,
    onEvent: handleRealtimeEvent,
    clientName: 'order_genie_email_inbox',
  });

  return (
    <div className="container-fluid py-3 px-3 px-md-4">
      <div className="d-flex align-items-start justify-content-between mb-3 flex-wrap gap-2">
        <div>
          <div className="d-flex align-items-center gap-2">
            <h5 className="mb-0">
              <i className="bi bi-envelope me-2 text-primary" />
              OrderX Inbox
            </h5>
            {accountId && (
              <span
                className={`badge ${isConnected ? 'bg-success' : 'bg-secondary'} bg-opacity-10 ${isConnected ? 'text-success' : 'text-secondary'}`}
                style={{ fontSize: '0.7em' }}
                title={isConnected ? 'Live updates active' : 'Connecting to live updates…'}
              >
                <i className={`bi bi-circle-fill me-1`} style={{ fontSize: '0.5em' }} />
                {isConnected ? 'Live' : 'Connecting…'}
              </span>
            )}
          </div>
          {(account?.name || mailboxEmail) && (
            <div className="text-muted small mt-1">
              {account?.name}
              {account?.name && mailboxEmail && <span className="mx-1">·</span>}
              {mailboxEmail && (
                <span title="Monitored mailbox address">
                  <i className="bi bi-envelope-at me-1" />{mailboxEmail}
                </span>
              )}
            </div>
          )}
        </div>
        <a href="/order-x/mailbox-setup" className="btn btn-sm btn-outline-secondary">
          <i className="bi bi-envelope-gear me-1" />Mailbox Setup
        </a>
      </div>

      <EmailInboxSyncBanner
        status={syncStatus}
        onSyncNow={handleSyncNow}
        syncing={syncing}
      />

      <EmailInboxList refreshKey={refreshKey} />

      {/* Deep-link: auto-open a specific email item from ?item= URL param */}
      {deepLinkedItemId && (
        <EmailInboxDetail
          itemId={deepLinkedItemId}
          onClose={() => setDeepLinkedItemId(null)}
          onDeleted={() => { setDeepLinkedItemId(null); setRefreshKey((k) => k + 1); }}
          onRestored={() => { setDeepLinkedItemId(null); setRefreshKey((k) => k + 1); }}
        />
      )}
    </div>
  );
}
